import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Box, Button, Link, Tooltip, Typography } from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import ReportIcon from '@mui/icons-material/Report';

import DisconnectModal from '../shared/DisconnectModal';
import { toCamelCase, strToLocaleString } from '@/javascript/helpers/helpers';
import contaAzulLogo from '@/images/conta_azul_logo.png';
import InfoCard from '@/components/shared/boxes/InfoCard';
import Paragraph from '@/components/shared/typography/Paragraph';
import ErrorBox from '@/components/shared/boxes/ErrorBox';
import MainTooltip from '@/components/shared/others/MainTooltip';
import GrayBox from '@/components/shared/boxes/GrayBox';
import GreenBox from '@/components/shared/boxes/GreenBox';
import SyncErrorsModal from '../shared/SyncErrorsModal';

const ContaAzulCard = () => {
  const [showSyncErrorsModal, updateSyncErrorsModal] = useState(false);
  const [showDisconnectModal, updateDisconnectModal] = useState(false);
  const [apiError, setApiError] = useState({});
  const [data, setData] = useState({
    connectionStatus: undefined,
    contaAzulAccessToken: undefined,
    contaAzulRefreshToken: undefined,
    lastIntegrationAt: undefined,
    syncStatus: undefined
  });
  const [stateCode, setStateCode] = useState('');
  const [permissions, setPermissions] = useState({});
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [showSyncDoneBox, setShowSyncDoneBox] = useState(false)
  const windowRef = useRef(null)
  const windowIntervalRef = useRef(null)
  const intervalRef = useRef(null)
  const integrationStatus = data?.connectionStatus

  const stopPolling = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  };

  const fetchPoolingData = async (url) => {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    const contentType = response.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError("Oops, we haven't got JSON!");
    }

    const responseJson = await response.json();

    if (!response.ok) {
      const errorMessage = `Error ${response.status}: ${responseJson.error}`;
      return setApiError({
        errorMessage: errorMessage,
      });
    }

    updateDataAfterAPIRequest(responseJson);
  };

  const startPolling = () => {
    stopPolling();
    intervalRef.current = setInterval(() => fetchPoolingData(permissions?.integrationStatus?.href), 10000); // Poll every 10
  };

  const retrySync = async () => {
    try {
      const response = await axios.post('/integrations/erp/conta_azul/update_data.json')
      updateDataAfterAPIRequest(response.data);
    } catch (error) {
      const errorMessage = `Error ${error.status}: ${error.message}`;
      return setApiError({ errorMessage: errorMessage })
    }
  }

  useEffect(() => {
    setStateCode(Math.random().toString(36).substring(7));
    const fetchData = async () => {
      try {
        const response = await axios.get('/integrations/erp/conta_azul.json')
        updateDataAfterAPIRequest(response.data);
      } catch (error) {
        const errorMessage = `Error ${error.status}: ${error.message}`;
        return setApiError({ errorMessage: errorMessage })
      }
    };

    fetchData();
    setIsPollingEnabled(true);

    return () => {
      stopPolling();
    };
  }, []);

  useEffect(() => {
    if (permissions?.integrationStatus?.available) {
      if (isPollingEnabled) {
        startPolling();
        setIsPollingEnabled(false);
      }
    } else {
      stopPolling();
    }
  }, [permissions?.integrationStatus?.available, isPollingEnabled]);

  useEffect(() => {
    if (data?.syncStatus === 'updating')
      setShowSyncDoneBox(true)
  }, [data?.syncStatus])

  const updateDataAfterAPIRequest = (responseJson) => {
    const responsePermissions = {};
    responseJson.links.forEach((link) => {
      responsePermissions[toCamelCase(link.rel)] = {
        ...link,
        available: link.available !== undefined ? link.available : true,
      };
    });

    const apiData = {
      connectionStatus: toCamelCase(responseJson?.status || 'not_connected'),
      syncStatus: toCamelCase(responseJson?.sync_status || 'unknown'),
    };

    if (responseJson?.access_token)
      apiData.contaAzulAccessToken = responseJson.access_token

    if (responseJson?.refresh_token)
      apiData.contaAzulRefreshToken = responseJson.refresh_token

    if (responseJson?.last_integration_at)
      apiData.lastIntegrationAt = responseJson.last_integration_at

    setData({ ...data, ...apiData });
    setPermissions(responsePermissions);
  };

  const updateDataAfterAPIRequestAndAllowPooling = (responseJson) => {
    updateDataAfterAPIRequest(responseJson);
    setIsPollingEnabled(true);
  };

  function defineDisabledConnectionButtonText(buttonEnabled, loadingDisabledText = '') {
    if (integrationStatus === 'running') {
      return loadingDisabledText;
    }

    if (!buttonEnabled) {
      return 'Você não tem o módulo “Integração com ERP” contratado';
    }
  }

  const configureButtonEnable = permissions?.configureIntegration?.available;
  const loadingDisabledText = 'Aguarde a integração ser concluída';

  const configureButtonDisabledTitle = defineDisabledConnectionButtonText(configureButtonEnable, loadingDisabledText);

  const headerComponentLayout = {
    running: () => (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <RotateLeftIcon color="info" />
        <Box>
          <Typography color="info.main">
            <strong>Conectando...</strong>
          </Typography>
          <Paragraph>
            Por favor, aguarde enquanto estamos recebendo e enviando dados entre os sistemas.
          </Paragraph>
        </Box>
      </Box>
    ),
    done: () => (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <CheckIcon color="success" />
        <Box>
          <Typography color="success.main">
            <strong>Conectado</strong>
          </Typography>
        </Box>
      </Box>
    ),
    pendingWithErrors: () => (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <WarningIcon color="warning" />
        <Box>
          <Typography color="warning.main">
            <strong>Conectado com erro</strong>
          </Typography>
          <Paragraph>Alguns itens estão impedindo que os sistemas se conectem com sucesso.</Paragraph>
          <Link onClick={() => updateSyncErrorsModal(true)} component="button" color="warning.main">
            <strong>Visualizar erros</strong>
          </Link>
        </Box>
      </Box>
    ),
    notSynchronizedWithErrors: () => (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <ReportIcon color="error" />
        <Box>
          <Typography color="error.main">
            <strong>Não conectado</strong>
          </Typography>
          <Paragraph>Os dados de autenticação informados são inválidos.</Paragraph>
          {permissions?.configureIntegration && (
            <Link onClick={openOAuthPopup} component="button" color="error">
              <strong>Revisar dados</strong>
            </Link>
          )}
        </Box>
      </Box>
    ),
    notConnected: () => (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <ReportIcon color="error" />
        <Box>
          <Typography color="error.main">
            <strong>Não conectado</strong>
          </Typography>
          <Paragraph>
            Erro interno no servidor do ERP, caso o problema persista entrar em contato com o suporte do sistema.
          </Paragraph>
        </Box>
      </Box>
    ),
  };

  const additionalInfoLayout = {
    done: () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
        <GreenBox title="Envio de dados automático">
          <Typography>
            O Produttivo envia automaticamente dados para o ERP a cada nova alteração.
          </Typography>
        </GreenBox>
        <GrayBox title="Recebimentos de dados manual">
          <Paragraph>
            O ERP não envia para o Produttivo as alterações feitas em seu sistema. <br />
            Para garantir que os itens estejam sincronizados, clique no botão abaixo.
          </Paragraph>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '40px', marginTop: '8px' }}>
              <Tooltip
                arrow
                placement="bottom"
                title={Boolean(permissions?.updateData) ? '' : 'Sem permissão para realizar a ação.'}>
                <span>
                  <Button
                    variant="contained"
                    size="medium"
                    color="success"
                    onClick={retrySync}
                    disabled={data?.syncStatus === 'updating' || !Boolean(permissions?.updateData)}
                  >
                    Receber dados do ERP
                  </Button>
                </span>
              </Tooltip>
              <Box>
                <Typography sx={{ color: 'textPrimary.main' }}>
                  <strong>Última atualização de dados:</strong>
                </Typography>
                {strToLocaleString(data.lastIntegrationAt)}
              </Box>
            </Box>
            {syncStatusLayout[data?.syncStatus] && syncStatusLayout[data?.syncStatus]()}
          </Box>
        </GrayBox>
      </Box>
    ),
  }

  const syncStatusLayout = {
    updating: () => (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <RotateLeftIcon color="info" />
        <Box>
          <Typography color="info.main">
            <strong>Atualizando...</strong>
          </Typography>
          <Paragraph>
            Por favor, aguarde enquanto estamos recebendo dados do ERP.
          </Paragraph>
        </Box>
      </Box>
    ),
    done: () => showSyncDoneBox && (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <CheckIcon color="success" />
        <Box>
          <Typography color="success.main">
            <strong>Atualizado</strong>
          </Typography>
          <Paragraph>
            Todos os dados cadastrados no ERP estão registrados no Produttivo.
          </Paragraph>
        </Box>
      </Box>
    )
  }

  const canSeeVisibleAndDisconnectButtons =
    integrationStatus && integrationStatus !== 'notContracted' && integrationStatus !== 'notSynchronized';

  const openPopup = () => {
    const host = window.location.origin
    const redirect_uri = `${host}${permissions?.redirectIntegration?.href}`
    const url = `${permissions?.login?.href}&redirect_uri=${redirect_uri}&state=${stateCode}`
    return window.open(url)
  }

  const openOAuthPopup = () => {
    windowRef.current = openPopup()
    window.addEventListener('message', handleMessageListener)
    windowIntervalRef.current = setInterval(() => {
      const popupClosed = !windowRef.current || !windowRef.current.window || windowRef.current.window.closed;
      if (popupClosed) {
        clearInterval(windowIntervalRef.current);
        window.removeEventListener('message', handleMessageListener);
      }
    }, 1000);
  }

  const handleMessageListener = async (message) => {
    const defaultErrorMessage = 'Erro ao vincular à Conta Azul, tente novamente mais tarde.'
    try {
      const stateMessage = message && message.data && message.data.state
      if (stateCode === stateMessage) {
        const code = message && message.data && message.data.code
        if (code) {
          const response = await axios.post('/integrations/erp/conta_azul/oauth_token', { code }, { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } })
          updateDataAfterAPIRequestAndAllowPooling(response.data)
        } else {
          setApiError({ errorMessage: defaultErrorMessage })
        }
      } else {
        setApiError({ errorMessage: defaultErrorMessage })
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || defaultErrorMessage
      setApiError({ errorMessage: `Error ${error?.status}: ${errorMessage}` })
    } finally {
      window.removeEventListener('message', handleMessageListener)
    }
  }

  return (
    <>
      {apiError.errorMessage && <ErrorBox errorMessage={apiError.errorMessage} onClose={() => setApiError({})} />}
      <InfoCard
        img={contaAzulLogo}
        ImageProps={{
          sx: { height: '120px', width: '120px' },
        }}
        linkTitle="Saiba mais"
        linkHref="https://help.produttivo.com.br/pt-BR/articles/11378725-como-configurar-a-integracao-com-a-conta-azul"
        linkOptions={{
          targetBlank: true,
          thirdPartyLink: true,
        }}
        headerComponent={headerComponentLayout[integrationStatus] && headerComponentLayout[integrationStatus]()}
        bodyComponent={
          <Box sx={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {integrationStatus !== 'done' && permissions?.configureIntegration && (
              <MainTooltip title={configureButtonDisabledTitle}>
                <Box sx={{ display: 'inline-block' }}>
                  <Button
                    variant="contained"
                    size="medium"
                    color="success"
                    onClick={openOAuthPopup}
                    disabled={!configureButtonEnable}
                  >
                    Configurar
                  </Button>
                </Box>
              </MainTooltip>
            )}
            {canSeeVisibleAndDisconnectButtons && permissions?.disconnectIntegration?.available && (
              <>
                <MainTooltip title={!permissions?.disconnectIntegration?.available ? loadingDisabledText : undefined}>
                  <Box sx={{ display: 'inline-block' }}>
                    <Button
                      variant="outlined"
                      size="medium"
                      color="success"
                      onClick={() => updateDisconnectModal(true)}
                      disabled={!permissions?.disconnectIntegration?.available || data?.syncStatus === 'updating'}
                    >
                      Desconectar
                    </Button>
                  </Box>
                </MainTooltip>
              </>
            )}
          </Box>
        }
        additionalInfoComponent={additionalInfoLayout[integrationStatus] && additionalInfoLayout[integrationStatus]()}
      />
      <SyncErrorsModal
        modalOpen={showSyncErrorsModal}
        onClose={(v) => updateSyncErrorsModal(v)}
        onSuccess={(resp) => updateDataAfterAPIRequestAndAllowPooling(resp)}
        tryAgainApiMethod={permissions?.retrySync?.type}
        tryAgainApiUrl={permissions?.retrySync?.href}
        errorsHref={{
          recentErrorHref: permissions?.recentErrorsList?.href,
          customerErrorHref: permissions?.customerErrorsList?.href,
          partErrorHref: permissions?.partErrorsList?.href,
          serviceErrorHref: permissions?.serviceErrorsList?.href,
          workErrorHref: permissions?.workErrorsList?.href,
        }}
        canRetrySync={permissions?.retrySync}
      />
      <DisconnectModal
        modalOpen={showDisconnectModal}
        onClose={(v) => updateDisconnectModal(v)}
        onSuccess={(resp) => updateDataAfterAPIRequestAndAllowPooling(resp)}
        apiUrl={permissions?.disconnectIntegration?.href}
        apiMethod={permissions?.disconnectIntegration?.type}
      />
    </>
  );
};

export default ContaAzulCard;
